(function () {
    'use strict';

    // Classic baseline scripts expose currentScript; a Vite ES module instead
    // reads the same public route configuration from its explicit footer marker.
    var script = document.currentScript || document.querySelector('script[data-live-visitor]');
    if (!script || !script.dataset.endpoint || !script.dataset.pageRoute) {
        return;
    }

    var endpoint = script.dataset.endpoint;
    var pageRoute = script.dataset.pageRoute;
    var sessionName = script.dataset.sessionName || 'PHPSESSID';
    // Let page resources and post-load initialization settle before tracking.
    var initialHeartbeatDelay = 5000;
    var heartbeatInterval = 25000;
    var initialRetryInterval = 15000;
    var maximumRetryInterval = 120000;
    var requestTimeout = 8000;
    var retryTimer = null;
    var idleCallback = null;
    var activeRequest = null;
    var requestPending = false;
    var consecutiveFailures = 0;
    var running = false;
    var nextHeartbeatAt = null;
    var body = 'page_route=' + encodeURIComponent(pageRoute) +
        '&session_name=' + encodeURIComponent(sessionName);
    var beaconBody = null;

    function isPrerendering() {
        if ('prerendering' in document) {
            return document.prerendering;
        }

        return false;
    }

    function canSendHeartbeat() {
        return running && document.visibilityState !== 'hidden' &&
            !isPrerendering() && navigator.onLine !== false;
    }

    function clearScheduledHeartbeat() {
        if (retryTimer !== null) {
            window.clearTimeout(retryTimer);
            retryTimer = null;
        }

        if (idleCallback !== null && 'cancelIdleCallback' in window) {
            window.cancelIdleCallback(idleCallback);
            idleCallback = null;
        }
    }

    function retryDelay() {
        if (consecutiveFailures === 0) {
            return heartbeatInterval;
        }

        return Math.min(
            initialRetryInterval * Math.pow(2, Math.min(consecutiveFailures - 1, 3)),
            maximumRetryInterval
        );
    }

    function scheduleHeartbeat(delay) {
        if (!canSendHeartbeat()) {
            return;
        }

        clearScheduledHeartbeat();
        nextHeartbeatAt = Date.now() + delay;

        if (delay > 0) {
            retryTimer = window.setTimeout(function () {
                retryTimer = null;
                scheduleHeartbeat(0);
            }, delay);
            return;
        }

        if ('requestIdleCallback' in window) {
            idleCallback = window.requestIdleCallback(function () {
                idleCallback = null;
                sendHeartbeat();
            }, {timeout: 3000});
        } else {
            retryTimer = window.setTimeout(function () {
                retryTimer = null;
                sendHeartbeat();
            }, 0);
        }
    }

    function completeRequest(success) {
        activeRequest = null;
        requestPending = false;
        consecutiveFailures = success ? 0 : consecutiveFailures + 1;
        nextHeartbeatAt = Date.now() + retryDelay();
        resume();
    }

    function sendWithXmlHttpRequest() {
        var request = null;
        var settled = false;
        var finish = function (success) {
            if (settled) {
                return;
            }

            settled = true;
            completeRequest(success);
        };

        try {
            request = new XMLHttpRequest();
            activeRequest = {
                abort: function () {
                    finish(false);
                    request.abort();
                }
            };
            request.open('POST', endpoint, true);
            request.withCredentials = true;
            request.timeout = requestTimeout;
            request.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded;charset=UTF-8');
            request.onload = function () {
                finish(request.status >= 200 && request.status < 300);
            };
            request.onerror = request.ontimeout = request.onabort = function () {
                finish(false);
            };
            request.send(body);
        } catch {
            finish(false);
        }
    }

    function sendHeartbeat() {
        if (!canSendHeartbeat()) {
            return;
        }

        if (requestPending) {
            return;
        }

        requestPending = true;

        // Beacon has no response callback and lets the browser prioritize this
        // small background POST. A typed body preserves PHP's form parsing.
        if (typeof navigator.sendBeacon === 'function' && typeof Blob === 'function') {
            try {
                if (beaconBody === null) {
                    beaconBody = new Blob([body], {
                        type: 'application/x-www-form-urlencoded;charset=UTF-8'
                    });
                }

                if (navigator.sendBeacon(endpoint, beaconBody)) {
                    // Accepted into the browser queue, not an HTTP success.
                    completeRequest(true);
                    return;
                }
            } catch {
                // A blocked/unavailable Beacon must not affect the storefront.
            }
        }

        if (typeof window.fetch !== 'function') {
            sendWithXmlHttpRequest();
            return;
        }

        var settled = false;
        var abortController = 'AbortController' in window ? new AbortController() : null;
        var timeout = null;
        var finish = function (success) {
            if (settled) {
                return;
            }

            settled = true;
            window.clearTimeout(timeout);
            completeRequest(success);
        };
        var abort = function () {
            // Settle before aborting so late promises cannot affect a restored
            // page or clear its newer pending request.
            finish(false);
            if (abortController !== null) {
                abortController.abort();
            }
        };
        timeout = window.setTimeout(abort, requestTimeout);
        activeRequest = {abort: abort};

        var options = {
            method: 'POST',
            credentials: 'same-origin',
            cache: 'no-store',
            keepalive: true,
            priority: 'low',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
            },
            body: body
        };

        if (abortController !== null) {
            options.signal = abortController.signal;
        }

        try {
            window.fetch(endpoint, options).then(function (response) {
                return response.ok;
            }, function () {
                return false;
            }).then(finish);
        } catch {
            finish(false);
        }
    }

    function start() {
        if (running || isPrerendering()) {
            return;
        }

        running = true;
        if (nextHeartbeatAt === null) {
            nextHeartbeatAt = Date.now() + initialHeartbeatDelay;
        }

        resume();
    }

    function resume() {
        if (running && !requestPending) {
            scheduleHeartbeat(Math.max(0, nextHeartbeatAt - Date.now()));
        }
    }

    function pause() {
        clearScheduledHeartbeat();

        if (activeRequest && typeof activeRequest.abort === 'function') {
            activeRequest.abort();
        }
    }

    function stop() {
        running = false;
        pause();
    }

    if (document.readyState === 'complete') {
        start();
    } else {
        window.addEventListener('load', start, {once: true});
    }

    if (isPrerendering()) {
        document.addEventListener('prerenderingchange', function () {
            if (document.readyState === 'complete') {
                start();
            }
        }, {once: true});
    }

    window.addEventListener('pageshow', function (event) {
        if (event.persisted) {
            start();
        }
    });

    document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'visible') {
            resume();
        } else {
            pause();
        }
    });

    window.addEventListener('offline', pause);
    window.addEventListener('online', resume);
    window.addEventListener('pagehide', stop);
}());
